import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import debounce from "lodash.debounce"

// helpers
import { hasOverflow } from "app/static/frontend/imports/desktopHelperFunctions"

/** useTruncated
 * Hook to determine if a component has truncated its text
 * Attach the ref this hook returns to the element to measure
 * Returns:
 * [
    - ref: ref to be attached to a component to measure the truncation state of via (<Comp ref={ref} />
    - isTruncated: bool holding whether or not the element is truncated
 * ]
 */
export const useTruncated = () => {
    const ref = useRef()
    const [isTruncated, updateIsTruncated] = useState(false)
    useEffect(() => {
        if (ref.current) {
            const offsetWidth = ref.current.offsetWidth
            const scrollWidth = ref.current.scrollWidth

            const scrollHeight = ref.current.scrollHeight
            const clientHeight = ref.current.clientHeight
            updateIsTruncated(
                // true if using normal css / single line truncation
                offsetWidth < scrollWidth ||
                    // true if using webkit multi-line truncation
                    clientHeight + 1 < scrollHeight,
                // The +1 here is used to fight a firefox bug where
                // the scroll height would change by 1 pixel between renders when
                // experiencing no text trunaction, possibly due to implementation
                // of -webkit-line-clamp or similar
            )
        }
    })
    return [ref, isTruncated]
}

/** useHover
 * Hook to determine if the user is hovering over the given ref
 * Returns:
 * [
    - ref: ref to be attached to a component to see hover state (<Comp ref={ref} />
    - isHovered: bool holding whether or not the user is hovering over the ref
 * ]
 */
export const useHover = () => {
    const [isHovered, setHovered] = useState(false)
    const ref = useRef(null)

    const handleMouseOver = () => setHovered(true)
    const handleMouseOut = () => setHovered(false)

    useEffect(() => {
        const node = ref.current

        if (node) {
            node.addEventListener("mouseover", handleMouseOver)
            node.addEventListener("mouseout", handleMouseOut)

            return () => {
                node.removeEventListener("mouseover", handleMouseOver)
                node.removeEventListener("mouseout", handleMouseOut)
            }
        }
    }, [ref.current])

    return [ref, isHovered]
}

/** useDebouncedHover (use this for a smooth, debounced hover experience. For example on the resourcetables)
 * Hook to determine if the user is hovering over the given ref
 * Args: debounceTime -> amount of time to debounce
 * Returns:
 * [
    - divRef: ref to be attached to a component to see the hover state (<Comp ref={ref} />
    - isHovered: bool holding whether or not the user is hovering over the ref
 * ]
 */
export const useDebouncedHover = (debounceTime = 300) => {
    const [hovered, setHovered] = useState(false)
    const divRef = useRef()

    const handleMouseOver = () => debouncedSetHovered(true)
    const handleMouseOut = () => debouncedSetHovered(false)

    useEffect(() => {
        const node = divRef.current

        if (node) {
            node.addEventListener("mouseenter", handleMouseOver)
            node.addEventListener("mouseleave", handleMouseOut)

            return () => {
                node.removeEventListener("mouseenter", handleMouseOver)
                node.removeEventListener("mouseleave", handleMouseOut)
            }
        }
    }, [divRef.current])

    // The below is to make the hovering text functionality nice and smooth.
    // We 1) debounce the hover trigger, and 2) invalidate false hover state aka a "stuck button".
    const debouncedSetHovered = useCallback(
        debounce((v) => setHovered(v), debounceTime),
        [],
    )

    // https://github.com/facebook/react/issues/4492#issuecomment-569410248
    if (hovered && divRef.current && divRef.current.matches(":hover") === false) {
        setHovered(false)
    }

    return [divRef, hovered]
}

export const useRefOverflow = () => {
    const reference = useRef()
    const [overflows, setOverflows] = useState({})

    // the onResize function we are binding to the resize window event
    // will use the overflows state it had at the moment it was bound (on componentDidMount)
    // because the callback of addEventListener is executed in its own closure

    // to solve this, we need to save the state inside a ref,
    // that way it will always be current (even inside the onResize addEventListener closure)

    // 🤢
    // https://stackoverflow.com/a/56554056/6201291
    const overflowsRef = useRef({})
    // overflowsRef is used as a global reference/pointer to the overflows state variable,
    // whose initial value is all we have access to in the onResize addEventListener closure
    // (the overflowsRef, on the other hand, will always contain the most up to date overflow and overflowWidth state variables)
    overflowsRef.current = overflows

    useEffect(() => {
        const onResize = debounce(() => {
            if (reference && reference.current) {
                const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth

                if (overflowsRef.current.overflow && width >= overflowsRef.current.overflowWidth) {
                    setOverflows({
                        overflow: false,
                        overflowWidth: 0,
                    })
                }
                if (hasOverflow(reference.current)) {
                    setOverflows({
                        overflow: true,
                        overflowWidth: width,
                    })
                }
            }
        }, 250)

        if (reference && reference.current) {
            window.addEventListener("resize", onResize)
            onResize()
        }

        // componentWillUnmount
        return () => window.removeEventListener("resize", onResize)
        // componentDidMount
    }, [])

    return {
        overflow: overflows.overflow,
        reference,
    }
}

/**
 *  @name useLockBodyScroll
 *
 * Prevents users from being able to scroll the page. Useful when you have a modal covering the screen,
 * and being able to scroll the content behind it would be weird.
 *
 * Can be called from the modal component with no arguments or from the parent component with the isDisabled argument -
 * in case the modal is not a function component.
 *
 * Adapted from {@link https://usehooks.com/useLockBodyScroll/}
 *
 * @param {Boolean} isDisabled Whether the scroll should disabled.
 */
export const useLockBodyScroll = (isDisabled) => {
    useLayoutEffect(() => {
        // Get original body overflow
        const originalStyle = window.getComputedStyle(document.body).overflow

        // Prevent scrolling on mount
        if (isDisabled === "undefined") {
            document.body.style.overflow = "hidden"
        }

        // Prevent scrolling when passed arg
        if (isDisabled) {
            document.body.style.overflow = "hidden"
        }

        // Re-enable scrolling when component unmounts
        return () => {
            document.body.style.overflow = originalStyle
        }
    }, [isDisabled])
}

/**
 * @name useOnClickOutside
 *
 * Fires the provided callback function when it detects a click outside of the ref component.
 * Borrowed from: https://usehooks.com/useOnClickOutside/ under the Unlicense.
 *
 * Wrapping the handler function in a `useCallback()` before passing into this hookcan prevent
 * the `useEffect()` hook below from running more than once! :zap:
 *
 * @param {Object} ref Component to check for clicks outside of
 * @param {Function} handler Function to call when click detected
 * @param {Array} customEvents Events to listen for outside the component; "mousedown" and "touchstart" by default
 */
export const useOnClickOutside = (ref, handler, customEvents = ["mousedown", "touchstart"]) => {
    useEffect(() => {
        const listener = (event) => {
            // Do nothing if clicking ref's element or descendent elements
            if (!ref.current || ref.current.contains(event.target)) {
                return
            }

            handler(event)
        }

        customEvents.forEach((eventName) => {
            document.addEventListener(eventName, listener)
        })

        return () => {
            customEvents.forEach((eventName) => {
                document.removeEventListener(eventName, listener)
            })
        }
    }, [ref, handler])
}

/**
 * @name useOnClickOutsideMultipleRefs
 *
 * A spin on OnClickOutside to take in multiple refs as an array.
 *
 * @param {Array} refArray Array of components to check for clicks outside of
 * @param {Function} handler Function to call when click detected
 * @param {Array} customEvents Events to listen for outside the component; "mousedown" and "touchstart" by default
 */
export const useOnClickOutsideMultipleRefs = (refArray, handler, customEvents = ["mousedown", "touchstart"]) => {
    useEffect(() => {
        if (!Array.isArray(refArray)) {
            return
        }

        const listener = (event) => {
            // Do nothing if clicking refArray's element or descendent elements
            let returnSansHandling = false
            if (refArray.length > 0) {
                refArray.forEach((r) => {
                    if (!r.current || r.current.contains(event.target) || r.current.contains(event.target.firstChild)) {
                        returnSansHandling = true
                        return
                    }
                })
            }

            if (returnSansHandling) {
                return
            }

            handler(event)
        }

        customEvents.forEach((eventName) => {
            document.addEventListener(eventName, listener)
        })

        return () => {
            customEvents.forEach((eventName) => {
                document.removeEventListener(eventName, listener)
            })
        }
    }, [refArray, handler])
}

/**
 * @name useEscapeKey
 *
 * Fires callback when the "Escape" key is used. Useful keyboard navigation component
 * when using above `useOnClickOutside` to close a modal or popdown.
 *
 * @param {Function} handler Function to call when Escape is pressed then released
 */
export const useEscapeKey = (handler) => {
    const handleEscKey = useCallback(
        (event) => {
            if (event.key === "Escape") {
                handler()
            }
        },
        [handler],
    )
    useEffect(() => {
        document.addEventListener("keyup", handleEscKey, false)

        return () => {
            document.removeEventListener("keyup", handleEscKey, false)
        }
    }, [handleEscKey])
}

/**
 * @name usePrevious
 *
 * Keeps track of values through re-renders. Useful for preserving the previous props of a component.
 *
 * Borrowed from the official React Docs {@link https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state}
 *
 * @param {any} value value to keep track of
 * @returns {any} The value previously set
 */
export const usePrevious = (value) => {
    const ref = useRef()

    useEffect(() => {
        ref.current = value
    })

    return ref.current
}
